/**
 * QL-212 — how a stored risk-assessment row seeds the widget.
 *
 * A `flow_instance_component_value` row for `risk_assessment` holds two blobs:
 * `inputData` (what was answered) and `calculatedData` (what the server derived
 * from it). The widget seeds from a single merged blob, so every caller has to
 * combine them — and the rule is subtler than it looks:
 *
 * - The inputs seed the widget **whenever they exist**. A half-finished answer
 *   is still an answer, and losing it on reload is what client story 5 forbids.
 * - The calculated half is merged over them **only when it is not partial**.
 *   A partial row carries `score: 0` and placeholder names, which would seed the
 *   widget with a score the server never derived.
 * - `finalScore` / `isScoreCapped` likewise come only from a non-partial result.
 *   Handing the widget a null `finalScore` makes it fall back to its own matrix
 *   lookup, which knows nothing about the model's cap — so a capped client would
 *   be shown the higher number.
 *
 * `scoreRaw` / `score` stay INSIDE the returned value: the widget seeds its
 * internal matrix state from `scoreRaw ?? score`, which is the raw score, while
 * `finalScore` is the separate effective one. They are not interchangeable.
 *
 * Pure and total: malformed JSON on either side yields the empty seed rather
 * than throwing, because a widget that renders nothing is worse than a widget
 * that renders unanswered.
 */

export type RiskWidgetSeed = {
  /** The blob the widget seeds from, or null when nothing has been answered. */
  value: string | null;
  /** The persisted effective score — server-derived, cap included. */
  finalScore: number | null;
  /** Whether that effective score was clamped to the model's cap. */
  isScoreCapped: boolean;
};

const EMPTY_SEED: RiskWidgetSeed = { value: null, finalScore: null, isScoreCapped: false };

function parseObject(blob: string | null | undefined): Record<string, unknown> | null {
  if (!blob) return null;
  try {
    const parsed: unknown = JSON.parse(blob);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Build the widget's seed from a stored component value's two blobs.
 *
 * Used by the advisor's global page and by both public-flow renderers, so a
 * half-finished answer behaves identically wherever the client left it.
 */
export function buildRiskWidgetSeed(
  inputData: string | null | undefined,
  calculatedData: string | null | undefined,
): RiskWidgetSeed {
  const input = parseObject(inputData);
  if (!input) return EMPTY_SEED;

  const calculated = parseObject(calculatedData);
  // `isPartial` marks a row the server stored without scoring it — the answer is
  // recorded, the result is not yet real.
  const settled = calculated && calculated.isPartial !== true ? calculated : null;

  return {
    value: JSON.stringify({ ...input, ...(settled ?? {}) }),
    finalScore: typeof settled?.score === "number" ? settled.score : null,
    isScoreCapped: settled?.isCapped === true,
  };
}
